/**
 * Rollback Service
 *
 * Provides automatic rollback functionality for OTA updates that cause crashes.
 * Tracks crash loops and restores the previous version if the app repeatedly crashes
 * shortly after startup.
 *
 * Crash Loop Detection:
 * - If the app crashes 3 times within 3 minutes, it's considered a crash loop
 * - Crashes are detected by checking if the app was forcefully terminated
 * - On rollback, the previous version bundle is restored and auto-updates are disabled
 *
 * Lifecycle:
 * 1. App starts → Check crash count
 * 2. If crash loop detected → Rollback to previous version
 * 3. If stable > 5 minutes → Clear crash tracking
 * 4. Before update → Save current version info
 * 5. After update → Monitor for crashes
 */

import type { IPlatformService } from '~/services/platform/types';

const CRASH_LOG_KEY = 'efflux_crash_log';
const PREVIOUS_VERSION_KEY = 'efflux_previous_version';
const UPDATE_DISABLED_KEY = 'efflux_updates_disabled';

const CRASH_LOOP_THRESHOLD = 3; // Number of crashes
const CRASH_LOOP_WINDOW_MS = 3 * 60 * 1000; // 3 minutes
const STABLE_RUN_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export interface CrashRecord {
  timestamp: string;
  version: string;
}

export interface PreviousVersionInfo {
  version: string;
  bundlePath: string;
  timestamp: string;
}

export interface RollbackResult {
  rolledBack: boolean;
  reason?: string;
  previousVersion?: string;
}

export class RollbackService {
  private platformService: IPlatformService;
  private startupTime: number;
  private stabilityTimer: NodeJS.Timeout | null = null;

  constructor(platformService: IPlatformService) {
    this.platformService = platformService;
    this.startupTime = Date.now();

    console.log('[Rollback] Service initialized');
  }

  /**
   * Initialize crash detection on app startup.
   * Checks if we're in a crash loop and triggers rollback if needed.
   */
  async init(): Promise<RollbackResult> {
    console.log('[Rollback] Checking for crash loops...');

    // Record this startup
    this.recordStartup();

    // Check for crash loop
    const crashLoop = this.detectCrashLoop();

    if (crashLoop) {
      console.warn('[Rollback] Crash loop detected! Initiating rollback...');
      return await this.performRollback();
    }

    // Start stability timer to clear crash log if app runs stable
    this.startStabilityTimer();

    return { rolledBack: false };
  }

  /**
   * Save current version info before installing an update.
   * This allows us to rollback if the new version causes crashes.
   */
  saveCurrentVersion(version: string, bundlePath: string): void {
    const info: PreviousVersionInfo = {
      version,
      bundlePath,
      timestamp: new Date().toISOString(),
    };

    try {
      localStorage.setItem(PREVIOUS_VERSION_KEY, JSON.stringify(info));
      console.log('[Rollback] Saved current version for rollback', {
        version,
        bundlePath,
      });
    } catch (error) {
      console.error('[Rollback] Failed to save version info', error);
    }
  }

  /**
   * Check if auto-updates are currently disabled due to rollback.
   */
  areUpdatesDisabled(): boolean {
    try {
      const disabled = localStorage.getItem(UPDATE_DISABLED_KEY);
      return disabled === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Re-enable auto-updates after manual intervention.
   */
  enableUpdates(): void {
    try {
      localStorage.removeItem(UPDATE_DISABLED_KEY);
      console.log('[Rollback] Auto-updates re-enabled');
    } catch (error) {
      console.error('[Rollback] Failed to enable updates', error);
    }
  }

  /**
   * Record app startup in crash log.
   */
  private recordStartup(): void {
    try {
      const crashLog = this.getCrashLog();
      const currentVersion = this.getCurrentVersion();

      const record: CrashRecord = {
        timestamp: new Date().toISOString(),
        version: currentVersion,
      };

      crashLog.push(record);

      // Keep only recent crashes (last hour)
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      const recentCrashes = crashLog.filter(
        (crash) => new Date(crash.timestamp).getTime() > oneHourAgo,
      );

      localStorage.setItem(CRASH_LOG_KEY, JSON.stringify(recentCrashes));

      console.log('[Rollback] Recorded startup', {
        version: currentVersion,
        totalCrashes: recentCrashes.length,
      });
    } catch (error) {
      console.error('[Rollback] Failed to record startup', error);
    }
  }

  /**
   * Detect if we're in a crash loop.
   * Returns true if there have been CRASH_LOOP_THRESHOLD crashes within
   * CRASH_LOOP_WINDOW_MS milliseconds.
   */
  private detectCrashLoop(): boolean {
    try {
      const crashLog = this.getCrashLog();

      if (crashLog.length < CRASH_LOOP_THRESHOLD) {
        return false;
      }

      // Get the last N crashes
      const recentCrashes = crashLog.slice(-CRASH_LOOP_THRESHOLD);
      const oldestCrash = new Date(recentCrashes[0].timestamp).getTime();
      const newestCrash = new Date(
        recentCrashes[recentCrashes.length - 1].timestamp,
      ).getTime();

      const timeDiff = newestCrash - oldestCrash;

      if (timeDiff < CRASH_LOOP_WINDOW_MS) {
        console.warn('[Rollback] Crash loop detected!', {
          crashes: CRASH_LOOP_THRESHOLD,
          window: `${CRASH_LOOP_WINDOW_MS / 1000}s`,
          timeDiff: `${timeDiff / 1000}s`,
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('[Rollback] Failed to detect crash loop', error);
      return false;
    }
  }

  /**
   * Perform rollback to previous version.
   */
  private async performRollback(): Promise<RollbackResult> {
    try {
      const previousVersion = this.getPreviousVersionInfo();

      if (!previousVersion) {
        console.error('[Rollback] No previous version info available');
        return {
          rolledBack: false,
          reason: 'No previous version info available',
        };
      }

      console.log(
        '[Rollback] Rolling back to version',
        previousVersion.version,
      );

      // Disable auto-updates to prevent re-installing the bad version
      localStorage.setItem(UPDATE_DISABLED_KEY, 'true');

      // Install previous version
      await this.platformService.installUpdate(previousVersion.bundlePath);

      // Clear crash log to reset tracking
      this.clearCrashLog();

      console.log('[Rollback] Rollback successful - restarting app...');

      // Restart app
      setTimeout(() => {
        this.platformService.restartApp();
      }, 1000);

      return {
        rolledBack: true,
        previousVersion: previousVersion.version,
        reason: 'Crash loop detected',
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('[Rollback] Rollback failed', errorMessage);
      return {
        rolledBack: false,
        reason: `Rollback failed: ${errorMessage}`,
      };
    }
  }

  /**
   * Start stability timer.
   * If the app runs for STABLE_RUN_DURATION_MS without crashing,
   * we clear the crash log.
   */
  private startStabilityTimer(): void {
    this.stabilityTimer = setTimeout(() => {
      console.log(
        '[Rollback] App stable - clearing crash log and old version info',
      );
      this.clearCrashLog();
      this.clearPreviousVersionInfo();
    }, STABLE_RUN_DURATION_MS);
  }

  /**
   * Get crash log from localStorage.
   */
  private getCrashLog(): CrashRecord[] {
    try {
      const stored = localStorage.getItem(CRASH_LOG_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Clear crash log from localStorage.
   */
  private clearCrashLog(): void {
    try {
      localStorage.removeItem(CRASH_LOG_KEY);
      console.log('[Rollback] Crash log cleared');
    } catch (error) {
      console.error('[Rollback] Failed to clear crash log', error);
    }
  }

  /**
   * Get previous version info from localStorage.
   */
  private getPreviousVersionInfo(): PreviousVersionInfo | null {
    try {
      const stored = localStorage.getItem(PREVIOUS_VERSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  /**
   * Clear previous version info from localStorage.
   */
  private clearPreviousVersionInfo(): void {
    try {
      localStorage.removeItem(PREVIOUS_VERSION_KEY);
      console.log('[Rollback] Previous version info cleared');
    } catch (error) {
      console.error('[Rollback] Failed to clear version info', error);
    }
  }

  /**
   * Get current app version.
   */
  private getCurrentVersion(): string {
    try {
      return import.meta.env.VITE_APP_VERSION || '1.0.0';
    } catch {
      return '1.0.0';
    }
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }

    console.log('[Rollback] Service destroyed');
  }
}

export default RollbackService;
